import { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { Loader2, Wifi } from 'lucide-react';

/**
 * Adaptive video player with HLS.js + MP4 fallback.
 *
 * Behaviour matches Netflix/YouTube-class players:
 *  - Try HLS master playlist first (auto-quality switching)
 *  - If transcoding still in progress (425) OR HLS not supported on browser,
 *    fall back to direct MP4 stream with HTTP Range
 *  - Pre-buffer ~30s ahead so seek + playback feel instant
 *  - Show small badge indicating active quality (e.g. "1080p · AUTO")
 */
const HlsVideoPlayer = ({ mediaId, token, mp4StreamUrl, autoPlay = true, onClose }) => {
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const [activeRendition, setActiveRendition] = useState(null);
  const [transcoding, setTranscoding] = useState(false);
  const [hlsReady, setHlsReady] = useState(false);

  useEffect(() => {
    if (!mediaId || !token) return;
    const video = videoRef.current;
    if (!video) return;

    const hlsUrl = `/api/media/${mediaId}/hls/master.m3u8?auth=${encodeURIComponent(token)}`;

    // Cleanup helper — runs on every effect re-run + unmount
    const teardown = () => {
      if (hlsRef.current) {
        try { hlsRef.current.destroy(); } catch (e) { /* ignore */ }
        hlsRef.current = null;
      }
    };

    // Helper: bind to native HLS (Safari iOS) — single line, browser handles everything
    const setNativeSrc = () => {
      video.src = hlsUrl;
      setHlsReady(true);
    };

    const setMp4Fallback = () => {
      video.src = mp4StreamUrl;
      setHlsReady(true);
    };

    // Probe the manifest first so we know whether the server has HLS ready.
    // Use a tiny GET (just the master m3u8 — ~200 bytes) instead of HEAD because
    // some CDN edges drop HEAD requests for non-static paths (return 405).
    fetch(hlsUrl, { method: 'GET' })
      .then((res) => {
        if (res.status === 425) {
          // Transcoding still in progress — show toast + use MP4 fallback (range stream)
          setTranscoding(true);
          setMp4Fallback();
          return;
        }
        if (!res.ok) {
          setMp4Fallback();
          return;
        }
        // HLS ready
        setTranscoding(false);
        if (Hls.isSupported()) {
          const hls = new Hls({
            // Pre-buffer aggressively for smooth UX (Netflix-class)
            maxBufferLength: 60,        // 60s of forward buffer
            maxMaxBufferLength: 120,    // hard cap
            backBufferLength: 30,       // keep 30s behind for instant rewind
            // Faster startup — start at the lowest rendition and switch up
            startLevel: -1,             // auto
            capLevelToPlayerSize: true, // never download 1080p in a 480-wide player
            enableWorker: true,         // off-main-thread decryption + parsing
            lowLatencyMode: false,      // VOD content
          });
          hlsRef.current = hls;
          hls.loadSource(hlsUrl);
          hls.attachMedia(video);
          hls.on(Hls.Events.MANIFEST_PARSED, () => {
            setHlsReady(true);
            if (autoPlay) video.play().catch(() => {});
          });
          hls.on(Hls.Events.LEVEL_SWITCHED, (_, data) => {
            try {
              const level = hls.levels?.[data.level];
              if (level) setActiveRendition(`${level.height}p`);
            } catch (e) { /* ignore */ }
          });
          hls.on(Hls.Events.ERROR, (_, data) => {
            // Fatal errors: try MP4 fallback once
            if (data?.fatal) {
              teardown();
              setMp4Fallback();
            }
          });
        } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
          // Safari has native HLS — far better than hls.js polyfill
          setNativeSrc();
        } else {
          setMp4Fallback();
        }
      })
      .catch(() => {
        // Network/probe failed — direct MP4 still works for partial files
        setMp4Fallback();
      });

    return teardown;
  }, [mediaId, token, mp4StreamUrl, autoPlay]);

  return (
    <div className="relative w-full bg-black" data-testid="hls-video-player">
      {!hlsReady && (
        <div className="absolute inset-0 flex items-center justify-center text-white/80 text-xs pointer-events-none z-10">
          <Loader2 size={16} className="animate-spin mr-2" /> CARREGANDO PLAYER...
        </div>
      )}
      <video
        ref={videoRef}
        className="w-full max-h-[80vh] block"
        controls
        playsInline
        preload="auto"
        data-testid="video-player"
      />
      {/* Status badges */}
      <div className="absolute top-2 left-2 flex items-center gap-1 pointer-events-none">
        {transcoding && (
          <span className="bg-amber-600/90 text-white text-[9px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider flex items-center gap-1" data-testid="badge-transcoding">
            <Loader2 size={9} className="animate-spin" />
            OTIMIZANDO QUALIDADE
          </span>
        )}
        {!transcoding && activeRendition && (
          <span className="bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded font-mono" data-testid="badge-rendition">
            <Wifi size={9} className="inline mr-1" />
            {activeRendition} · AUTO
          </span>
        )}
      </div>
    </div>
  );
};

export default HlsVideoPlayer;
